/**
 * The Explore Map's area key (v3 design review: the map itself carries no
 * text). One row per area, in the same order as `HexLayout.areas`
 * (ascending area `code`, Unassigned last when present): title, age
 * ("today" / "3d" / "never"), and tile count. No colour swatch — v3's
 * monochrome map tells areas apart by cluster geometry and hover, not
 * colour, so the key doesn't need one either. Hovering a row (or a tile on
 * the map) highlights that area; keyboard focus on a row does the same
 * (`onFocus`/`onBlur` mirror `onMouseEnter`/`onMouseLeave`), so the
 * highlight isn't mouse-only. Clicking a row (except Unassigned, which
 * isn't a real area) opens it in Product Map.
 *
 * Rendered to the right of the map's `<svg>` on `md`+ viewports, below it on
 * narrow ones — the parent's `flex flex-col md:flex-row` handles that; this
 * component only sets its own fixed width for the row-column case.
 *
 * @module features/exploration/components/ExplorationMapKey
 */

import { Link } from '@tanstack/react-router';
import { Text } from '@chatsdet/ui';

export type ExplorationMapKeyRow = {
  areaId: string | null;
  label: string;
  tileCount: number;
  age: string;
};

type Props = {
  areas: ExplorationMapKeyRow[];
  propertyId: string;
  highlightAreaId: string | null;
  onHighlight: (areaId: string | null) => void;
};

export function ExplorationMapKey({ areas, propertyId, highlightAreaId, onHighlight }: Props) {
  return (
    <div className="w-full shrink-0 space-y-0.5 md:w-64">
      {areas.map((row) => {
        const isHighlighted = highlightAreaId === row.areaId;
        const rowClassName = `flex items-start justify-between gap-2 rounded-md px-2 py-1.5 transition-colors ${
          isHighlighted ? 'bg-muted' : 'hover:bg-muted/60'
        }`;
        const content = (
          <>
            <Text size="sm" className="min-w-0 break-words leading-tight">
              {row.label}
            </Text>
            <Text size="xs" className="shrink-0 whitespace-nowrap font-mono text-muted-foreground">
              {row.age} · {row.tileCount}
            </Text>
          </>
        );

        return (
          <div key={`key-${row.areaId ?? 'unassigned'}`}>
            {row.areaId ? (
              <Link
                to="/product-map"
                search={{ area: row.areaId, propertyId, view: 'explore' }}
                className={rowClassName}
                onMouseEnter={() => onHighlight(row.areaId)}
                onMouseLeave={() => onHighlight(null)}
                onFocus={() => onHighlight(row.areaId)}
                onBlur={() => onHighlight(null)}
              >
                {content}
              </Link>
            ) : (
              <div className={rowClassName}>{content}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
